import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { Modal, ModalProps } from '@vimily/bonjoro-ui-components-v2/Modal';
import { useGlobalEvent } from 'beautiful-react-hooks';
import DragAndDropFileSelector from '../../MyLibrary/Components/DragAndDropUpload/DragAndDropFileSelector';
import cx from 'classnames';
import {
  RichText,
  RichTextEditor,
  SlateData,
  defaultEmptyRichTextValue,
  getRichTextFromSlateData,
  parseRichTextToString,
  parseStringToRichText,
} from '@vimily/bonjoro-ui-components-v2/RichTextEditor';
import { VideoPlayer, VideoPlayerPlayPausePlacement } from '@vimily/bonjoro-ui-components-v2/VideoPlayer';
import { Contact, Transcript } from '~/types/models';
import ManageContactModal from '../../ManualUpload/Components/ManageContactModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { TagBar } from '../../Tags/Components/TagBar';
import {
  Tag,
  TaggableResourcePage,
  TaggableResourceType,
} from '~/types/models/tags';
import { UploadUrlResponse } from '~/types/api/recordings';
import axios from 'axios';
import { getThumbnailBlob } from '~/utils/thumbnail';
import useThumbnailRenderer from '~/App/Components/Record/use-thumbnail-renderer';
import { uploadVideoToS3 } from '~/App/Api/S3Upload';
import {
  UserAsset,
  createUserAsset,
  updateUserAsset,
  userAssetsApiUrl,
  userAssetsQueryKeys,
} from '../../MyLibrary/Api/UserAsset';
import { useQueryClient } from 'react-query';
import FlashContext from '~/App/Components/Flash/Context';
import { Tab, Tabs } from '@vimily/bonjoro-ui-components-v2/Tabs';
import { TranscriptReaderPanel } from './TranscriptReaderPanel';
import { updateResponse } from '~/Share/TestimonialLandingPage/Api/TestimonialRespondent';
import { queryKeys as testimonialRespondentQueryKeys } from '~/App/Api/TestimonialRespondent';
import { getUploadUrl as getTestimonialUploadUrl } from '~/Share/TestimonialLandingPage/Api/TestimonialRespondent';
import { testimonialRespondentsMediaQueryKeys } from '../../MyLibrary/Api/Testimonials';
import { TestimonialResponseType } from '~/types/models/testimonial';
import { stringifyTranscript } from '../util';

const defaultTitle = 'Add a new testimonial';

const openTestimonialAssetEditorModalEventName =
  'add-edit-testimonial-asset-modal::open';

export enum TestimonialAssetEditorModalType {
  AssetEditor = 'asset_editor',
  ManageContact = 'manage_contact',
}

export enum TestimonialAssetEditorModelType {
  TestimonialResponse = 'testimonial_response',
  UserAssets = 'user_assets',
}

export interface TestimonialAssetEditorModalEventDetails {
  type: TestimonialAssetEditorModelType;
  id?: string;
  title?: string;
  profile?: Contact;
  videoUrl?: string | null;
  videoBlob?: Blob | null;
  tags?: Tag[];
  text?: SlateData[] | null;
  transcript?: Transcript;
  modelProperties?: Record<string, unknown>;
  successCallback?: () => void;
}

export const openTestimonialAssetEditorModal = (
  detail: TestimonialAssetEditorModalEventDetails
) =>
  window.dispatchEvent(
    new CustomEvent<TestimonialAssetEditorModalEventDetails>(
      openTestimonialAssetEditorModalEventName,
      {
        detail,
      }
    )
  );

export interface TestimonialAssetEditorModalProps {
  children?: React.ReactNode;
}

export function TestimonialAssetEditorModal({
  children,
}: TestimonialAssetEditorModalProps) {
  const [activeModal, setActiveModal] =
    useState<TestimonialAssetEditorModalType>();
  const [type, setType] = useState<TestimonialAssetEditorModelType>();
  const [tab, setTab] = useState<number>(0);
  const [id, setId] = useState<string>();
  const [title, setTitle] = useState<string>(defaultTitle);
  const [videoUrl, setVideoUrl] = useState<string | null>();
  const [profile, setProfile] = useState<Contact>();
  const [videoBlob, setVideoBlob] = useState<Blob>();
  const [successCallback, setSuccessCallback] = useState<() => void>();

  const [tags, setTags] = useState<Tag[]>([]);
  /**
   * intended for the tag bar component, can't use the tags because it will result into circular dependency
   */
  const [defaultTags, setDefaultTags] = useState<Tag[]>([]);

  const [text, setText] = useState<RichText>(parseStringToRichText(''));
  const [transcript, setTranscript] = useState<Transcript>();
  const [loading, setLoading] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [modelProperties, setModelProperties] =
    useState<Record<string, unknown>>();

  const queryClient = useQueryClient();
  const { createFlash } = useContext(FlashContext);

  const onOpenTestimonialAssetEditorModal = useGlobalEvent<
    CustomEvent<TestimonialAssetEditorModalEventDetails | undefined>
  >(openTestimonialAssetEditorModalEventName as keyof WindowEventMap);

  onOpenTestimonialAssetEditorModal((event) => {
    setActiveModal(TestimonialAssetEditorModalType.AssetEditor);

    if (event?.detail?.id) {
      setId(event?.detail?.id);
    }

    if (event?.detail?.type) {
      setType(event?.detail?.type);
    }

    if (event?.detail?.title) {
      setTitle(event?.detail?.title);
    }

    if (event?.detail?.videoUrl) {
      setVideoUrl(event?.detail?.videoUrl);
    }

    if (event?.detail?.videoBlob) {
      setVideoBlob(event?.detail?.videoBlob);
    }

    if (event?.detail?.profile) {
      setProfile(event?.detail?.profile);
    }

    if (event?.detail?.tags) {
      setTags(event?.detail?.tags ?? []);
      setDefaultTags(event?.detail?.tags ?? []);
    }

    if (event?.detail?.text && event?.detail?.text != null) {
      setText(getRichTextFromSlateData(event?.detail?.text));
    }

    if (event?.detail?.transcript) {
      setTranscript(event?.detail?.transcript);
    }

    if (event?.detail?.modelProperties) {
      setModelProperties(event.detail?.modelProperties);
    }
    if (event?.detail?.successCallback) {
      setSuccessCallback(() => event.detail?.successCallback);
    }
  });

  const onModalClose = () => {
    setActiveModal(undefined);
    setId(undefined);
    setType(undefined);
    setTitle(defaultTitle);
    setVideoUrl(undefined);
    setProfile(undefined);
    setVideoBlob(undefined);
    setTranscript(undefined);
    setLoading(false);
    setTags([]);
    setDefaultTags([]);
    setText(defaultEmptyRichTextValue);
    setSuccessCallback(() => () => undefined);
  };

  const onFileUpload = (files: File[]) => {
    const file = files[0];
    setVideoBlob(file);

    if (file) {
      videoUrl && URL.revokeObjectURL(videoUrl);
      setVideoUrl(URL.createObjectURL(file));
    }

    return () => {
      videoUrl && URL.revokeObjectURL(videoUrl);
    };
  };

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    openTestimonialAssetEditorModal({
      type: TestimonialAssetEditorModelType.UserAssets,
      successCallback: () => {
        createFlash({
          id: 'testimonial-asset-edit-save-success',
          title: 'Success',
          message: 'Testimonial added!',
        });
      },
    });
  };

  useEffect(() => {
    document.addEventListener('dragover', handleDragOver);

    return () => {
      document.removeEventListener('dragover', handleDragOver);
    };
  }, []);

  const hasValidProfile = profile != undefined;

  const canSave =
    id ||
    parseRichTextToString(text ?? []).length > 0 ||
    videoBlob != undefined;

  const disableCta = !canSave || loading;

  const onAddProfileClick = () => {
    if (hasValidProfile) return;

    setActiveModal(TestimonialAssetEditorModalType.ManageContact);
  };

  const { renderThumbnail } = useThumbnailRenderer({ videoRef });

  /**
   * Generate the best thumbnail.
   * Safari has issue on rendering just black on it first frames when loaded
   */
  const generateThumbnail = (): Promise<string | undefined> => {
    return new Promise((resolve) => {
      let tries = 0;
      const MAX_TRIES = 5;
      const interval = setInterval(() => {
        if (!videoRef?.current) return;
        if (tries == MAX_TRIES) {
          resolve(undefined);
          clearInterval(interval);
        }

        videoRef.current.currentTime = +1;
        const { originalDataUrl: thumbnail } = renderThumbnail();
        if (thumbnail) {
          resolve(thumbnail);
          clearInterval(interval);
        }

        tries++;
      }, 500);
    });
  };

  const uploadUserAssetVideo = async (uuid: string) => {
    try {
      if (!videoBlob) return;

      const { data: uploadUrl } = await axios.get<UploadUrlResponse>(
        `${userAssetsApiUrl}/${uuid}/video-upload-url`
      );

      const thumbnail = await generateThumbnail();
      const thumbnailBlob = await getThumbnailBlob(thumbnail!);
      await uploadVideoToS3(uploadUrl.upload_url, videoBlob);

      const formData = new FormData();
      formData.append('thumbnail', thumbnailBlob, 'thumbnail.jpg');
      formData.append('_method', 'PUT');

      const updateUserAssetData = await axios.post<UserAsset>(
        `${userAssetsApiUrl}/${uuid}?single_upload=true`,
        formData
      );

      return updateUserAssetData.data;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error message:', e);
    }
  };

  const uploadTestimonialResponseVideo = async () => {
    try {
      if (!videoBlob) return;

      const upload = await getTestimonialUploadUrl({
        respondentId: modelProperties?.respondentId as string,
        questionId: modelProperties?.questionId as string,
      });

      const thumbnail = await generateThumbnail();
      const thumbnailBlob = await getThumbnailBlob(thumbnail!);
      await uploadVideoToS3(upload.url, videoBlob);

      return { thumbnail: thumbnailBlob, timestamp: upload.timestamp };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error message:', e);
    }
  };

  const onSaveClick = useCallback(async () => {
    if (!canSave) return;

    setLoading(true);

    if (type === TestimonialAssetEditorModelType.UserAssets) {
      if (!id) {
        const { data: userAsset } = await createUserAsset({
          content_rich: text,
          profile_uuid: profile?.id,
          tags: tags?.map((tag) => tag.uuid),
        });

        await uploadUserAssetVideo(userAsset.uuid);
      } else {
        const userAssetUuid = modelProperties?.userAssetUuid as string;

        await uploadUserAssetVideo(userAssetUuid);

        await updateUserAsset({
          uuid: userAssetUuid,
          content_rich: text,
          profile_uuid: profile?.id,
          tags: tags?.map((tag) => tag.uuid),
        });
      }

      queryClient.invalidateQueries(userAssetsQueryKeys.lists());

      successCallback?.();
    } else if (
      type === TestimonialAssetEditorModelType.TestimonialResponse &&
      id
    ) {
      const video = await uploadTestimonialResponseVideo();

      await updateResponse({
        textResponse: text,
        respondentId: modelProperties?.respondentId as string,
        questionId: modelProperties?.questionId as string,
        responseId: modelProperties?.responseId as string,
        thumbnail: video?.thumbnail,
        tags: tags?.map((tag) => tag.uuid),
        timestamp: video?.timestamp,
        type: TestimonialResponseType.Video, //don't matter anymore, this will be removed once we have supported mult response on tm landingpage
      });

      queryClient.invalidateQueries(
        testimonialRespondentQueryKeys.detail({
          respondentUuid: modelProperties!.respondentId as string,
          testimonialUuid: modelProperties!.testimonialUuid as string,
        })
      );

      queryClient.invalidateQueries(testimonialRespondentQueryKeys.lists());
      queryClient.invalidateQueries(
        testimonialRespondentsMediaQueryKeys.lists()
      );
    }

    setLoading(false);

    onModalClose();

    successCallback?.();
  }, [canSave, id, type, videoUrl, profile, videoBlob, tags, text]);

  const modalProps = useMemo<ModalProps>(() => {
    return {
      open: activeModal === TestimonialAssetEditorModalType.AssetEditor,
      onModalClose: !loading ? onModalClose : undefined,
      className: 'max-w-[900px] !p-[30px]',
      overlayClassName: '!z-[9950]',
      closeButtonClassName: '!w-5',
      hideActions: true,
    };
  }, [activeModal, loading]);

  const hasValidTranscript = transcript && stringifyTranscript(transcript) != '';

  const editorComponent = (
    <RichTextEditor
      value={text}
      onChange={setText}
      className='w-full h-full max-h-[400px]'
      placeholder='Enter testimonial'
    />
  );

  return (
    <>
      {children}
      <Modal {...modalProps}>
        <div className='flex flex-col gap-y-5'>
          <span className='v2-text-neutral-900 text-[21px] font-bold'>
            {title}
          </span>
          <div className='flex flex-row gap-x-2.5'>
            <div className='w-1/2 flex-1 flex flex-col gap-y-[15px]'>
              {videoUrl != undefined ? (
                <VideoPlayer
                  videoRef={videoRef}
                  videoUrl={videoUrl}
                  containerClassName='v2-animate-fade-in-150 rounded'
                  videoClassName='w-full h-full rounded max-h-[270px] !object-contain v2-bg-neutral-500'
                  hideClose
                  hideFullScreen
                  hideNewTab
                  showProgressBar
                  playPausePlacement={VideoPlayerPlayPausePlacement.Center}
                />
              ) : (
                <DragAndDropFileSelector
                  onFileUpload={onFileUpload}
                  title='Drop video file here to upload'
                  className={cx([
                    'w-full h-full flex flex-col gap-y-[15px] py-[48px]',
                    'rounded justify-center v2-bg-neutral-50 !v2-text-neutral-500',
                  ])}
                  iconClassName='!w-[50px] !h-[44px]'
                  titleClassName='!m-0 font-bold text-[21px]'
                  subtitleClassName='!m-0'
                  descriptionClassName='v2-text-neutral-500 !text-xs'
                />
              )}
              <div
                className={cx(
                  [
                    'flex flex-row gap-x-[15px] px-2.5 h-[60px]',
                    'items-center rounded v2-bg-neutral-50',
                  ],
                  {
                    'justify-center py-5 cursor-pointer': !hasValidProfile,
                    'justify-between py-2.5': hasValidProfile,
                  }
                )}
                onClick={onAddProfileClick}
              >
                {hasValidProfile ? (
                  <>
                    <div className='flex flex-row gap-x-5 items-center'>
                      <img
                        className='w-10 h-10 rounded-full'
                        src={profile?.image}
                      />
                      <div className='flex flex-col justify-center text-base v2-text-neutral-900'>
                        <span className='font-bold'>{profile?.name}</span>
                        {profile?.position && <span>{profile?.position}</span>}
                      </div>
                    </div>
                    {!id && (
                      <FontAwesomeIcon
                        icon={faTrash}
                        className='v2-text-neutral-500 cursor-pointer hover:v2-text-neutral-900'
                        onClick={() => setProfile(undefined)}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <i className='icon icon-sm icon-add-contact !v2-bg-neutral-500' />
                    <span className='text-base v2-text-neutral-500'>
                      Add a profile
                    </span>
                  </>
                )}
              </div>
              <TagBar
                className={'w-full'}
                emptyText={'Add a tag to manage your embeds'}
                emptyTextClassName='!text-xs'
                sourcePage={TaggableResourcePage.MyLibraryAll}
                resourceType={TaggableResourceType.TestimonialResponse}
                defaultTags={defaultTags}
                onChange={(tags) => {
                  setDefaultTags(tags);
                  setTags(tags);
                }}
              />
            </div>
            <div className='w-1/2 flex-1 flex flex-col gap-y-5'>
              {hasValidTranscript ? (
                <Tabs
                  id='testimonial-asset-editor-tab'
                  className='h-[90%]'
                  active={tab}
                  setActive={setTab}
                  tabHeaderClassName='!mb-0'
                  tabPanelClassName='!p-0 !h-full'
                  tabContentClassName='!h-full'
                  liButtonClassName='!h-[40px]'
                  hideBottomBorder
                >
                  <Tab
                    id='testimonial-asset-editor-transcription-tab'
                    title={
                      <div className='flex flex-row gap-x-2.5 items-center justify-center'>
                        <img
                          className='h-5 w-5'
                          src='https://cdn.bonjoro.com/assets/Flat-icons/transcript.png'
                        />
                        <span>Transcript</span>
                      </div>
                    }
                  >
                    <TranscriptReaderPanel
                      transcript={transcript}
                      className='max-h-[400px]'
                    />
                  </Tab>
                  <Tab
                    id='testimonial-asset-editor-transcription-tab'
                    title={
                      <div className='flex flex-row gap-x-2.5 items-center justify-center'>
                        <img
                          className='h-5 w-5'
                          src='https://cdn.bonjoro.com/assets/Flat-icons/quotes.png'
                        />
                        <span>Text</span>
                      </div>
                    }
                  >
                    {editorComponent}
                  </Tab>
                </Tabs>
              ) : (
                editorComponent
              )}
            </div>
          </div>
          <div className='flex flex-row justify-between'>
            <div className='flex flex-row gap-x-[15px]'>
              <Button
                variant={ButtonVariant.Secondary}
                className='!py-1.5'
                onClick={onModalClose}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
            <div className='flex flex-row gap-x-[15px]'>
              <Button
                variant={ButtonVariant.Primary}
                className='!py-1.5 flex flex-row gap-x-2.5 items-center justify-center'
                disabled={disableCta}
                onClick={onSaveClick}
              >
                {!loading && !id && 'Save'}
                {loading && !id && 'Saving'}
                {!loading && id && 'Update'}
                {loading && id && 'Updating'}
                {loading && <span className='fa fa-spinner spinning mr-2' />}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      <ManageContactModal
        open={activeModal === TestimonialAssetEditorModalType.ManageContact}
        searchModalHeader='Add a profile'
        ctaText='Add profile'
        editHeader='Edit a profile'
        addHeader='Add a profile'
        onRequestClose={() =>
          setActiveModal(TestimonialAssetEditorModalType.AssetEditor)
        }
        loading={false}
        initialValue={null}
        onContactAssign={(profile) => setProfile(profile)}
      />
    </>
  );
}
